<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
import StatusCodes from "./utils/ResponseCodes";
import { mapActions, mapGetters } from "vuex";
const dayInMs = 1000 * 60 * 60 * 24;

export default {
  name: "App",
  computed: mapGetters(["authenticated"]),
  methods: {
    async authenticate() {
      let URL = window.location.search.substring(1);
      const parameters = new URLSearchParams(URL);
      if (parameters.get("authenticated") && parameters.get("authenticated") === "ok") {
        this.setToken(parameters.get("token"));
        localStorage.setItem("expires-in", (Date.now() + dayInMs));
        if (!this.authenticated) this.fetchUserData();
      } else {
        var session = localStorage.getItem("session-data");
        if (session) session = JSON.parse(session);
        var expiration = localStorage.getItem("expires-in");
        if (expiration) expiration = parseInt(expiration);
        if (!session && !expiration) return;
        if (!expiration && session) return localStorage.removeItem("session-data");
        var isPastDue = Date.now() > expiration;

        if (isPastDue) {
          localStorage.removeItem("session-data");
          localStorage.removeItem("expires-in");
        }

        if (!isPastDue && session) {
          if (!this.authenticated) this.projectUserData(session);
        }
      }

      const queryAuthorization = parameters.get("authorization");
      if (queryAuthorization) {
        const authorization = atob(queryAuthorization);
        this.saveToken(authorization);
      }

      const token = this.readToken();

      if (token) {
        this.storeToken(token);
        this.$rest.token = token;
        const userPayload = await this.$rest.api.users("@me").get({ query: { with_guilds: true } }).then((response) => response.payload).catch(() => {});

        if (userPayload.status_code === StatusCodes.SUCCESS && userPayload.found) this.storeUser(userPayload);
        else this.discardToken();

        this.markLoginAsQueried();
      } else {
        this.markLoginAsQueried();
      }
    },
    saveToken (token) {
      localStorage.setItem("token_value", token);
      localStorage.setItem("token_expiration", Date.now() + dayInMs);
    },
    discardToken () {
      localStorage.removeItem("token_value");
      localStorage.removeItem("token_expiration");
      this.logoutUser();
    },
    readToken () {
      const tokenExpirationTime = parseInt(localStorage.getItem("token_expiration"));
      if (!isNaN(tokenExpirationTime) && tokenExpirationTime < Date.now()) return this.discardToken();
      return localStorage.getItem("token_value");
    },
    logoutUser () {
      this.resetUserData();
      this.storeToken(null);
      this.resetUser();
    },
    ...mapActions(["setToken", "fetchUserData", "projectUserData", "resetUserData"]),
    ...mapActions(["storeToken", "storeUser", "resetUser", "markLoginAsQueried"])
  },
  mounted() {
    this.authenticate().catch(err => console.error("Authentication error: ", err));
  }
}
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");

  :root {
    --black-blue: #0d0c13;
    --grey-blue: #181524;
    --dark-blue: #13121A;
    --dark-dark-blue: #0F0E16;
    --light-grey-blue: #1E1A24;
    --saturated-blue: #1441d4;
    --blue: #446fff;
    --white: #ffffff;
    --green: #28a745;
    --red: #dc3545;
    --faint-dark: rgba(230, 205, 205, 0.1);
    --gray: #3b3b3d;
    --grey: #789;
    --light-gray: #cccccc;
    --darker: rgba(0,0,0,0.7);
    --shadow-dark: rgba(0,0,0,0.5);
    --overlay-dark: rgba(0,0,0,0.96);
    --plain-gray: #9c9c9c;
    --blurple: #7289DA;
    --purple: #7928ca;
    --golden: #dfa32b;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: "Poppins", sans-serif;
  }

  body {
    background-color: var(--dark-blue)!important;
    color: var(--white);
  }

  a {
    color: var(--blue);
    text-decoration: none;
  }

  a:hover {
    color: var(--white);
    text-decoration: none;
  }

  .link {
    color: var(--saturated-blue);
    text-decoration: none;
    transition: all .2s;
  }

  .link:hover {
    filter: brightness(80%);
    text-decoration: underline;
  }


  textarea:focus,
  input:focus {
    outline: none;
  }

  p {
    font-size: .9em;
    line-height: 1.6em;
  }

  .text-small {
    font-size: .75em;
    line-height: 1.3em;
  }

  ul {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  .info-page-loader {
    height: 40vh;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .page-loader {
    height: 100vh;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .page-loader .ball-pulse div, .info-page-loader .ball-pulse div {
    width: 10px;
    height: 10px;
  }

  .gscni-btn {
    position: relative!important;
    left: 0em!important;
    width: 100%!important;
    margin-top: 5px;
  }

  .button-primary {
    color: var(--white);
    text-decoration: none;
    border: 1px solid var(--blue);
    padding: 10px;
    border-radius: 3px;
    position: relative;
    top: -1.8em;
    left: 2em;
    transition: 0.5s;
  }

  .button-secondary {
    color: var(--white);
    text-decoration: none;
    border: 1px solid var(--blue);
    padding: 10px;
    border-radius: 3px;
    transition: 0.5s;
    text-align: center;
    cursor: pointer;
  }

  .button-success {
    color: var(--white);
    text-decoration: none;
    border: 1px solid var(--green);
    padding: 10px;
    border-radius: 3px;
    transition: 0.5s;
    text-align: center;
    cursor: pointer;
  }

  .button-success:hover {
    border: 1px solid var(--white);
    background-color: var(--white);
    color: var(--green);
    transition: 0.5s;
  }

  .button-primary:hover, .button-secondary:hover {
    border: 1px solid var(--white);
    background-color: var(--white);
    color: var(--blue);
    transition: 0.5s;
  }

  .button-green {
    color: var(--white);
    text-decoration: none;
    border: 1px solid var(--green);
    padding: 10px;
    border-radius: 3px;
    position: relative;
    top: -1.8em;
    left: 2em;
    transition: 0.5s;
  }

  .button-green:hover {
    border: 1px solid var(--white);
    background-color: var(--white);
    color: var(--green);
    transition: 0.5s;
  }

  .button-disabled {
    color: var(--white);
    text-decoration: none;
    border: 1px solid var(--red);
    padding: 10px;
    border-radius: 3px;
    position: relative;
    top: -1.8em;
    left: 2em;
    transition: 0.5s;
  }

  .button-disabled:hover {
    border: 1px solid var(--white);
    background-color: var(--white);
    color: var(--red);
    transition: 0.5s;
  }

  .head-title {
    text-align: center;
    margin-top: 2em;
    margin-bottom: 2em;
  }

  .head-title-underline {
    text-align: center;
    margin-top: 2em;
  }

  .head-title-under-line-child {
    text-align: center;
    position: relative;
    top: 0.3em;
    margin-bottom: 3em;
  }

  .form-field-text-input {
    width: 100%;
    height: 40px;
    background-color: var(--black-blue);
    height: 3em!important;
    padding: 0.50em 0.60em;
    color: var(--white);
    border: solid 1px var(--gray);
    border-radius: 3px;
  }

  .field-input-error {
    border: solid 1px var(--red)!important;
  }

  .form-field-text-input:focus {
    border-color: var(--blue);
  }

  .field-input-error:focus {
    border-color: var(--red)!important;
  }

  .form-field-title-description {
    margin-bottom: 10px;
    font-size: 15px;
    color: var(--grey);
  }

  .form-field-textarea {
    max-width: 100%;
    min-width: 100%;
    max-height: 120px;
    min-height: 120px;
    width: 100%;
    height: 120px;
    background-color: var(--black-blue);
    border: 1px solid var(--gray);
    border-radius: 3px;
    color: var(--white);
    padding: 0.50em 0.60em;
  }

  .form-field-textarea:focus {
    border-color: var(--blue);
  }

  .select2-container--default .select2-selection--multiple .select2-selection__rendered {
    background-color: var(--black-blue);
  }

  .select2-container--default .select2-selection--multiple {
    background-color: var(--black-blue)!important;
    color: var(--white)!important;
    border: 1px solid var(--gray)!important;
  }

  .select2-results {
    background-color: var(--black-blue)!important;
  }

  .select2-selection__choice {
    background: var(--black-blue)!important;
    border: 1px solid var(--gray)!important;
    border-radius: 3px!important;
  }

  .select2-search__field {
    color: var(--white)!important;
  }

  .switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
  }

  .form {
    margin: 2em;
  }

  .form-field {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
  }

  .form-field-title {
    font-size: 18px;
  }

  .switch .checkbox {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--light-gray);
    -webkit-transition: .4s;
    transition: .4s;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: var(--white);
    -webkit-transition: .4s;
    transition: .4s;
  }

  .checkbox:checked+.slider {
    background-color: var(--blue);
  }

  .checkbox:focus+.slider {
    box-shadow: 0 0 1px var(--blue);
  }

  .checkbox:checked+.slider:before {
    -webkit-transform: translateX(26px);
    -ms-transform: translateX(26px);
    transform: translateX(26px);
  }

  .slider.round {
    border-radius: 34px;
  }

  .slider.round:before {
    border-radius: 50%;
  }

  .switch-label {
    position: relative;
    left: 0.3em;
    top: 0.3em;
    font-weight: bold;
  }

  .alert {
    padding: 20px;
    background-color: var(--red);
    color: var(--white);
    margin-bottom: 15px;
  }

  .clickable {
    cursor: pointer;
  }

  .closebtn {
    margin-left: 15px;
    color: white;
    font-weight: bold;
    float: right;
    font-size: 22px;
    line-height: 20px;
    cursor: pointer;
    transition: 0.3s;
  }

  .closebtn:hover {
    color: bla
  }

  #alerts {
    text-align: center;
    margin-right: 2em;
    margin-left: 2em;
  }

  .form-button {
    cursor: pointer;
    color: var(--white);
    text-decoration: none;
    border: 1px solid var(--blue);
    padding: 7px;
    border-radius: 3px;
    position: relative;
    transition: 0.5s;
    height: 40px;
    text-align: center;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
  }

  .form-button:hover {
    border: 1px solid var(--white);
    background-color: var(--white);
    color: var(--blue);
    transition: 0.5s;
  }

  .margin-left-btn {
    margin-left: 0.4em;
  }

  .green-alert {
    padding: 20px;
    background-color: var(--green);
    color: var(--white);
    margin-bottom: 15px;
  }

  .home-center {
    text-align: center;
  }

  @media only screen and (max-width: 990px) {
    .command-col .command-details {
      filter: none;
    }
  }

  .basic-select {
    background-color: var(--dark-blue);
    position: relative;
    color: var(--white);
    border: solid 1px var(--gray);
    border-radius: 3px;
  }

  .basic-select:hover {
    border-color: var(--blue);
  }

  .basic-select select {
    display: none;
  }

  .select-selected {
    background-color: var(--faint-dark);
  }

  .select-selected:after {
    position: absolute;
    content: "";
    top: 17px;
    right: 10px;
    width: 0;
    height: 0;
    border: 6px solid transparent;
    border-color: var(--white) transparent transparent transparent;
  }

  .select-selected.select-arrow-active:after {
    border-color: transparent transparent var(--white) transparent;
    top: 7px;
  }

  .select-items div,.select-selected {
    color: var(--white);
    padding: 10px;
    font-size: 13px;
    border-color: transparent transparent var(--faint-dark) transparent;
    cursor: pointer;
    z-index: 100;
    height: 38px;
  }

  .select-items {
    position: absolute;
    background-color: var(--black-blue);
    top: 100%;
    left: 0;
    right: 0;
    z-index: 99;
  }

  .select-hide {
    display: none;
  }

  .select-items div:hover, .same-as-selected {
    background-color: var(--black-blue);
  }

  .modal {
    transition: 0.5;
    display: none;
    position: fixed;
    z-index: 999;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: auto;
    background-color: rgba(17, 16, 23, 0.6);
  }

  .modal-content {
    background-color: var(--black-blue);
    margin: 5% auto;
    padding: 20px;
    border: 1px solid var(--blue);
    border-radius: 5px;
    width: 75%;
    height: auto;
  }

  .close {
    color: #ccc;
    float: right;
    font-size: 28px;
    font-weight: bold;
    position: relative;
    top: -0.3em;
    right: 0.3em;
  }

  .close:hover, .close:focus {
    color: var(--white);
    text-decoration: none;
    cursor: pointer;
  }

  .duration-number-input {
    float: left;
    width: 60%;
  }

  .duration-input-type {
    float: right;
    width: 39%;
  }

  @media only screen and (max-width: 970px) {
    .duration-input-type, .duration-number-input {
      float: none;
      width: 100%;
      margin-top: 1em;
      margin-left: 0;
    }

    .duration-number-input {
      margin-top: 0!important;
    }
  }

  #modalOpener {
    margin-left: 10%;
    margin-right: 10%;
    margin-bottom: 2.5em;
  }

  .option-list {
    margin-left: 2em;
    margin-right: 2em;
    font-size: 14px;
    margin-bottom: 3.4em;
  }

  .option-list-item {
    height: auto;
    width: auto;
    background-color: var(--faint-dark);
    padding: 1em;
    border-radius: 5px;
    margin-top: 2em;
    display: flex;
    align-items: center;
    min-height: 4.8em;
  }

  .description {
    color: var(--light-gray);
    font-size: 14px;
    width: 100%;
    height: auto;
    word-break: break-word;
    background-color: var(--faint-dark);
    border-radius: 3px;
    padding-bottom: 5px;
    padding-left: 5px;
    padding-right: 5px;
  }

  .empty-message {
    margin: 2.3em;
    text-align: center;
  }

  .little-margin {
    margin-bottom: 2.4em!important;
  }

  .basic-btn-red {
    border: 6px solid var(--red);
    background-color: var(--red);
    border-radius: 50%;
    color: var(--faint-dark);
    font-size: 20px;
    margin-right: 0.3em;
  }

  .basic-btn-red:hover {
    color: var(--faint-dark);
    transition: 0.5s;
  }

  .red-trashbin {
    position: relative;
    left: 3px;
    top: 3px;
  }

  .btn-icon {
    color: var(--white);
    text-decoration: none;
    border: 1px solid var(--red);
    padding: 9px;
    margin-right: 0.8em;
    border-radius: 3px;
    transition: 0.5s;
  }

  .btn-icon:hover {
    border: 1px solid var(--white);
    background-color: var(--white);
    color: var(--red);
    transition: 0.5s;
  }

  .confirmation {
    z-index: 2000;
    min-width: 85%;
    margin-right: 10%;
    margin-left: 7.5%;
    max-width: 1200px;
    border-radius: 3px;
    background-color: rgba(0,0,0,0.7);
    padding: 15px;
    display: -webkit-box;
    display: -moz-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: horizontal;
    -moz-box-orient: horizontal;
    -o-box-orient: horizontal;
    -webkit-flex-direction: row;
    -ms-flex-direction: row;
    flex-direction: row;
    -webkit-box-align: center;
    -moz-box-align: center;
    -o-box-align: center;
    -ms-flex-align: center;
    -webkit-align-items: center;
    align-items: center;
    position: fixed;
    bottom: 20px;
    animation: confirmShake .3s linear;
  }

  @keyframes confirmShake 
    {
      from {-webkit-transform: translate(0,100px);}
      60% {-webkit-transform: translate(0,-15px);}
      80% {-webkit-transform: translate(0,5px);}
      95% {-webkit-transform: translate(0px,-5px);}
      to {-webkit-transform: translate(0,0px);}
    }

  @-webkit-keyframes confirmShake 
  {
    from {-webkit-transform: translate(0,100px);}
    60% {-webkit-transform: translate(0,-15px);}
    80% {-webkit-transform: translate(0,5px);}
    95% {-webkit-transform: translate(0px,-5px);}
    to {-webkit-transform: translate(0,0px);}
  }

  @media (max-width: 665px) {
    .confirmation {
      width: unset;
      -webkit-box-orient: vertical;
      -moz-box-orient: vertical;
      -o-box-orient: vertical;
      -webkit-flex-direction: column;
      -ms-flex-direction: column;
      flex-direction: column;
    }
  }

  .confirmation .confirmation-red {
    background-color: #f04747;
    -webkit-transition: background-color 400ms ease-out;
    -moz-transition: background-color 400ms ease-out;
    -o-transition: background-color 400ms ease-out;
    -ms-transition: background-color 400ms ease-out;
    transition: background-color 400ms ease-out;
  }

  .confirmation .confirmation-enter {
    bottom: -100px;
  }

  .confirmation .confirmation-enter .confirmation-enter-active {
    bottom: 20px;
  }

  .confirmation-leave {
    bottom: 20px;
  }

  .confirmation .confirmation-leave .confirmation-leave-active {
    bottom: -100px;
  }

  .confirmation .confirmation-text {
    -webkit-box-flex: 1;
    -moz-box-flex: 1;
    -o-box-flex: 1;
    box-flex: 1;
    -webkit-flex: 1;
    -ms-flex: 1;
    flex: 1;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    margin-right: auto;
    display: flex;
  }

  @media (max-width: 640px) {
    .confirmation .confirmation-text {
      margin: auto;
    }
  }

  .confirmation .confirmation-buttons {
    display: -webkit-box;
    display: -moz-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: horizontal;
    -moz-box-orient: horizontal;
    -o-box-orient: horizontal;
    -webkit-flex-direction: row;
    -ms-flex-direction: row;
    flex-direction: row;
    margin-left: auto;
  }

  @media (max-width: 665px) {
    .confirmation .confirmation-buttons {
      margin: auto;
      margin-top: 5px;
    }
  }

  .confirmation .confirmation-buttons a {
    padding: 5px;
    margin-left: 10px;
  }

  .confirmation-primary {
    color: #fff;
    text-decoration: none;
    border: 1px solid var(--blue);
    padding: 10px;
    border-radius: 3px;
    transition: 0.5s;
  }

  .no-hover {
    cursor: unset!important;
  }

  .no-hover:hover {
    border: 1px solid var(--blue)!important;
    background-color: transparent!important;
    color: var(--white)!important;
    transition: 0.5s!important;
  }

  .confirmation-primary:hover {
    border: 1px solid var(--white);
    background-color: var(--white);
    color: var(--blue);
    transition: 0.5s;
  }

  .confirmation-discard, .button-danger {
    color: #fff;
    text-decoration: none;
    text-align: center;
    border: 1px solid var(--red);
    padding: 10px;
    border-radius: 3px;
    transition: 0.5s;
    cursor: pointer;
  }

  .confirmation-discard:hover, .button-danger:hover {
    border: 1px solid var(--white);
    background-color: var(--white);
    color: var(--red);
    transition: 0.5s;
  }

  .subtle-head-link {
    color: #7289DA;
    text-decoration: none;
    cursor: pointer;
    transition: .5s;
  }

  .subtle-head-link:hover {
    opacity: 0.65;
  }

  .spacer {
    min-width: 100%;
    height: 2em;
  }

  @supports (-moz-appearance:none) {
    .gscni-btn {
      position: relative!important;
      top: 0em;
    }
  }

  .c-name {
    top: 3px;
    position: relative;
  }

  .code {
    padding-left: 2px;
    padding-right: 2px;
    border-radius: 2px;
    color: #e83e8c;
    background-color: var(--white);
  }

  @-webkit-keyframes scale {
    0% {
      -webkit-transform: scale(1);
      transform: scale(1);
      opacity: 1;
    }
    45% {
      -webkit-transform: scale(0.1);
      transform: scale(0.1);
      opacity: 0.7;
    }
    80% {
      -webkit-transform: scale(1);
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes scale {
    0% {
      -webkit-transform: scale(1);
      transform: scale(1);
      opacity: 1;
    }
    45% {
      -webkit-transform: scale(0.1);
      transform: scale(0.1);
      opacity: 0.7;
    }
    80% {
      -webkit-transform: scale(1);
      transform: scale(1);
      opacity: 1; 
    }
  }

  .ball-pulse > div:nth-child(1) {
    -webkit-animation: scale 0.75s -0.24s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
    animation: scale 0.75s -0.24s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  }

  .ball-pulse > div:nth-child(2) {
    -webkit-animation: scale 0.75s -0.12s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
    animation: scale 0.75s -0.12s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  }

  .ball-pulse > div:nth-child(3) {
    -webkit-animation: scale 0.75s 0s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
    animation: scale 0.75s 0s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  }

  .ball-pulse > div {
    background-color: #fff;
    width: 10px;
    height: 10px;
    border-radius: 100%;
    margin: 2px;
    position: relative;
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
    display: inline-block;
  }

  .add-role {
    position: relative;
    top: 1.4em;
  }

  .remove-role {
    position: relative;
    top: 1.4em;
  }

  .cmd-channel {
  margin-top: 0.3em;
  }

  .select2-container {
    width: 52.5%!important;
  }

  .select2-container--default .select2-results__option--highlighted[aria-selected] {
    background-color: #292929!important;
  }

  .select2-container--default .select2-selection--multiple {
    min-height: 2.45em!important;
  }

  .select2-results__option[aria-selected] {
    background-color: #13121a!important;
  }

  .select2-dropdown {
    border: none!important;
  }

  .select2-container--default .select2-selection--multiple {
    width: 189.5%!important;
  }

  .highlighted-count {
    font-weight: bold;
    font-size: 1.5em;
  }

  @keyframes come-in {
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }

  @-webkit-keyframes scale {
    0% {
      -webkit-transform: scale(1);
      transform: scale(1);
      opacity: 1;
    }
    45% {
      -webkit-transform: scale(0.1);
      transform: scale(0.1);
      opacity: 0.7;
    }
    80% {
      -webkit-transform: scale(1);
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes scale {
    0% {
      -webkit-transform: scale(1);
      transform: scale(1);
      opacity: 1;
    }
    45% {
      -webkit-transform: scale(0.1);
      transform: scale(0.1);
      opacity: 0.7;
    }
    80% {
      -webkit-transform: scale(1);
      transform: scale(1);
      opacity: 1; 
    }
  }

  .page-pulse > div:nth-child(1) {
    -webkit-animation: scale 0.75s -0.24s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
    animation: scale 0.75s -0.24s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  }

  .page-pulse > div:nth-child(2) {
    -webkit-animation: scale 0.75s -0.12s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
    animation: scale 0.75s -0.12s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  }

  .page-pulse > div:nth-child(3) {
    -webkit-animation: scale 0.75s 0s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
    animation: scale 0.75s 0s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  }

  .page-pulse, .ball-pulse {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  .page-pulse > div {
    background-color: var(--white);
    width: 1.2em;
    height: 1.2em;
    border-radius: 100%;
    margin: 2px;
    position: relative;
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
    display: inline-block;
  }

  .style-chooser .vs__search::placeholder,
  .style-chooser .vs__dropdown-toggle,
  .style-chooser .vs__dropdown-menu {
    background: var(--faint-dark);
    border: none;
    color: var(--white);
  }

  .vs__search, .vs__search:focus, .vs__selected {
    color: var(--white)!important;
  }

  .style-chooser .vs__clear,
  .style-chooser .vs__open-indicator {
    fill: var(--white);
  }

  .vs--single .vs__selected {
    color: var(--white);
  }

  .vs--searchable .vs__dropdown-toggle {
    padding: 8px;
    background-color: var(--black-blue);
    height: 40px!important;
    border-radius: 3px;
    border: solid 1px var(--gray);
  }

  .vs--searchable .vs__dropdown-toggle:hover {
    border-color: var(--blue);
  }

  .vs__dropdown-option {
    background-color: var(--black-blue)!important;
    color: var(--white)!important; 
  }

  .vs__clear svg path,
  .vs__open-indicator {
    fill: var(--white)!important;
  }

  .vs__dropdown-menu {
    background-color: var(--black-blue);
  }

  .vs__search,
  .vs__search:focus {
    margin: 0!important;
    padding: 0!important;
  }

  .vs--single.vs--open .vs__selected {
    color: var(--white)!important;
  }

  .vs--single.vs--open .vs__selected {
    position: relative;
    bottom: 5px;
    left: -9px;
  }

  .vs--single .vs__selected {
    position: relative;
    bottom: 5px;
    left: -9px;
  }

  .vs__selected {
    background-color: transparent!important;
  }

  .vs__search, .vs__search:focus, .vs__selected {
    position: relative;
    top: -4px;
  }

  .vs__dropdown-menu {
    background-color: var(--black-blue)!important;
  }
  
  .outline-text {
    -webkit-text-fill-color: var(--blue);
    -webkit-text-stroke-width: 1px;
    -webkit-text-stroke-color: var(--blue);
  }

  .state-bar {
    background-color: var(--black-blue);
    margin: 0 2em;
    margin-top: 2.35em;
    height: 2.5em;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    border: 1px solid var(--white);
  }

  .state-bar a {
    color: var(--white);
    opacity: 1;
    transition: opacity .2s;
  }

  .state-bar a:hover {
    opacity: 0.8;
    text-decoration: underline;
  }

  .text-red {
    color: var(--red);
  }


  .background-blue {
    background-color: var(--blue);
  }

  .vs__dropdown-option:hover {
    background-color: var(--grey);
  }

  .text-info {
    color: var(--light-gray);
    font-size: 0.9em;
  }

  .red-icon {
    color: var(--red);
  }

  .green-icon {
    color: var(--green);
  }

  .input-error {
    font-size: 0.9em;
    color: var(--red)!important;
    margin-top: 0.5em;
  }

  .button-flex-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    justify-items: center;
  }

  .solid-button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4em;
    border: 1px solid rgba(0, 0, 0, 0);
    border-radius: 3px;
    font-size: 1em;
    cursor: pointer;
    transition: all .2s;
  }

  .big-button {
    padding: 0.6em;
  }

  .thin-button  {
    padding: 0.4em;
  }

  .small-button { 
    width: fit-content;
    padding: 0.2em 0.6em;
  }

  .solid-button:hover {
    filter: brightness(80%);
  }

  .background-purple {
    background-color: var(--purple);
  }

  .background-red {
    background-color: var(--red);
  }

  .color-white {
    color: var(--white);
  }

  .width-48 {
    width: 48%;
  }

  .button-inactive {
    color: var(--plain-gray);
    cursor: not-allowed;
  }

  .button-secondary.button-inactive:hover {
    color: var(--plain-gray);
    border-color: var(--blue);
    background-color: transparent;
  }

  .button-danger.button-inactive:hover {
    color: var(--plain-gray);
    border-color: var(--red);
    background-color: transparent;
  }

  .box {
    padding: 1em;
    border-radius: 3px;
  }

  .section-title {
    padding-bottom: 0.25em;
    border-bottom: 1px solid var(--gray);
    font-size: 1.6em;
  }

  .subscription-section {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .empty-section-container  {
    display: flex;
    flex-direction: column;
    gap: 0.8em;
    width: 100%;
    max-width: 24em;
    margin: 20vh 0;
  }

  .empty-section-icon {
    background-color: var(--light-gray);
    color: var(--plain-gray);
    width: 4em;
    height: 4em;
    border-radius: 5px;
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .empty-section-icon i {
    font-size: 1.6em;
  }

  .empty-section-title {
    font-size: 1.3em;
    font-weight: 550;
  }

  .empty-section-description {
    color: var(--grey);
  }

  .empty-section-button {
    margin-top: 20px;
    max-width: 16em;
  }

  .section-loader {
    margin-top: 2em;
  }

  .subscription-populated-list {
    width: 100%;
  }

  .subscription-populated-list .subscription-list {
    width: 100%;
    display: flex;
    align-items: center;
    align-content: center;
    flex-direction: column;
    transition: 0.5s;
  }

  .subscription-populated-list .subscription-list .subscription-item {
    background-color: var(--light-grey-blue);
    border-radius: 3px;
    padding: 1em;
    margin: 1em 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5em;
  }

  .subscription-populated-list .subscription-list .subscription-item .card-title-options {
    display: flex;
    justify-content: space-between;
  }

  .table-description {
    font-size: 1.05em;
    text-align: center;
    width: 100%;
    padding: 0 5%;
    color: var(--grey);
  }

  .table-section {
    text-align: center;
    width: 94%;
    margin-left: 3%;
    margin-right: 3%;
    margin-top: 2em;
    margin-bottom: 2em;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .table-section .table-title {
    font-size: 2.5em;
  }

  .object-shadow {
    -webkit-box-shadow: 2px 7px 19px 8px rgba(0,0,0,0.35); 
    box-shadow: 2px 7px 19px 8px rgba(0,0,0,0.35);
  }

  .table-section table {
    border-collapse: collapse;
    width: 90%;
    background-color: var(--dark-blue);
    margin-top: 3em;
    margin-bottom: 3em;
  }

  td, th {
    text-align: center;
    padding: 0.6em;
  }

  th {
    font-size: 1.4em;
  }

  tr:nth-child(even) {
    background-color: var(--grey-blue);
  }


  @media screen and (max-width: 1070px) {
    td, tr {
      font-size: 0.85em;
    }

    th {
      font-size: 1.1em;
    }
  }

  @media screen and (max-width: 525px) {
    td, th {
      padding: 0.4em;
    }

    td, tr {
      font-size: 0.85em;
    }

    th {
      font-size: 1.1em;
    }
  }

  @media screen and (min-width: 700px) {
    .subscription-populated-list .subscription-list {
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
    }
  }
</style>
